'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { WEBSITE_SIGN_IN_PATH } from '@/routes';
import posthog from 'posthog-js';
import { toast } from 'sonner';

import { safeIdentify } from '@repo/analytics';
import { appendPlatformHint, EXTENSION_PLATFORM_HINT } from '@repo/user-events';

import { env } from '@env';
import {
  POST_MESSAGE_DESTINATION_TYPES,
  POST_MESSAGE_ORIGIN_TYPES,
  POST_MESSAGE_TYPES,
  PostMessageType,
} from '@/@types/post-message.types';
import { Session, WebAppSessionType } from '@/@types/session.types';
import { isSessionProvenByAttempt } from '@/lib/auth-attempt-match';
import { useSession } from '@/providers/session-provider';

// ---------------------------------------------------------------------------
// Auth-telemetry — mirrored locally because the landing app cannot import
// from the website's `@/lib/analytics/auth-telemetry`. Same ULID format so
// the iframe-popup and skim.plus-popup flows stitch via the same
// `auth_attempt_id` carried in the OAuth URL's `aa` query param.
// ---------------------------------------------------------------------------

const ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

interface IframeAuthAttempt {
  id: string;
  surface: string;
  t0: number;
}

const generateUlid = (): string => {
  let timestamp = Date.now();
  const timeChars: string[] = [];
  for (let i = 9; i >= 0; i -= 1) {
    timeChars[i] = ULID_ALPHABET[timestamp % 32]!;
    timestamp = Math.floor(timestamp / 32);
  }
  const randomBytes = new Uint8Array(16);
  try {
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
      window.crypto.getRandomValues(randomBytes);
    } else {
      throw new Error('no crypto');
    }
  } catch {
    for (let i = 0; i < 16; i += 1) {
      randomBytes[i] = Math.floor(Math.random() * 256);
    }
  }
  const randomChars: string[] = [];
  for (let i = 0; i < 16; i += 1) {
    randomChars[i] = ULID_ALPHABET[randomBytes[i]! % 32]!;
  }
  return timeChars.join('') + randomChars.join('');
};

const safeCapture = (event: string, props?: Record<string, unknown>): void => {
  try {
    posthog.capture(event, props);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[landing useAuth] capture ${event} failed`, err);
  }
};

const buildIframeEventBase = (
  attempt: IframeAuthAttempt | null,
): Record<string, unknown> =>
  attempt
    ? {
        auth_attempt_id: attempt.id,
        surface: attempt.surface,
        flow: 'iframe_popup',
        ms_since_initiated: Math.max(0, Date.now() - attempt.t0),
      }
    : { flow: 'iframe_popup' };

export interface SignInOptions {
  /**
   * Surface that triggered the sign-in. Used as the primary dimension for
   * iframe-side funnel attribution. Examples: 'header_iframe',
   * 'wall_video_iframe', 'wall_article_iframe'.
   */
  surface?: string;
  /**
   * If set, also fires `login_wall_clicked` synchronously with the
   * matching `type` and `pathname`. Provider is unknown at this point
   * (the popup picks); join with the popup-side `signup_initiated`
   * (which has provider) on `distinct_id` to get a complete funnel.
   */
  wallType?: 'article' | 'video';
}

// Message format matching extension-webapp's auth-result
export interface AuthMessage {
  type: 'auth-result';
  success: boolean;
  session?: WebAppSessionType;
  error?: string;
}

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 600;

/**
 * Listener-grace window (CRE-783 / PR-1b — mirror of the extension-webapp
 * fix in CRE-780 / PR #266). When the popup closes (interval poll detects
 * `popup.closed === true`), stop polling immediately but leave the
 * `message` listener alive for `LISTENER_GRACE_MS` so a queued postMessage
 * can still arrive. Seam B (Pillsbury close-race) survives the cleanup
 * because of this grace.
 *
 * 1500ms matches the extension-webapp fix exactly so the silent-fail
 * latency is identical across both surfaces in the funnel.
 */
const LISTENER_GRACE_MS = 1500;

/**
 * Rescue refetch delay — wait this long after popup close + grace expire
 * before checking `/api/session`. Gives the iron-session cookie time to
 * land on `skim.plus` after the backend's Set-Cookie response. Empirically
 * 400ms is sufficient across typical desktop + mobile networks; mirror of
 * extension-webapp's `RESCUE_REFETCH_DELAY_MS`.
 */
const RESCUE_REFETCH_DELAY_MS = 400;

export const useAuth = () => {
  const { session, clearSession, setSession } = useSession();
  const pathname = usePathname();

  const [isPending, setIsPending] = useState(false);

  const listenerRef = useRef<
    ((event: MessageEvent<AuthMessage>) => void) | null
  >(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef<IframeAuthAttempt | null>(null);
  const graceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Handle for the rescue-refetch setTimeout so a new attempt (rapid re-click)
  // can cancel the prior attempt's in-flight rescue — otherwise the stale
  // timer fires, reads the NEW attempt's attemptRef, mis-attributes telemetry,
  // and clears the new attempt's spinner (RACE-1).
  const rescueTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * True once a result message has been processed (success OR error). Used
   * to dedupe the message-handler path against the grace-expiry rescue
   * path, and to skip the rescue when a message actually arrived.
   */
  const receivedRef = useRef(false);

  const cleanup = useCallback(() => {
    if (listenerRef.current) {
      window.removeEventListener('message', listenerRef.current);
      listenerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (graceTimeoutRef.current) {
      clearTimeout(graceTimeoutRef.current);
      graceTimeoutRef.current = null;
    }
    if (rescueTimeoutRef.current) {
      clearTimeout(rescueTimeoutRef.current);
      rescueTimeoutRef.current = null;
    }
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const handleSignIn = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      opts: SignInOptions = {},
    ) => {
      cleanup();
      receivedRef.current = false;
      setIsPending(true);

      const surface = opts.surface ?? 'extension_iframe';

      // Mint an auth_attempt_id for this iframe-popup flow. Embedded in
      // the OAuth URL via the website's `/sign-in?aa=` param (the popup
      // page reads it back). Stitches landing→popup→backend events
      // across origins.
      let attempt: IframeAuthAttempt | null = null;
      try {
        attempt = { id: generateUlid(), surface, t0: Date.now() };
      } catch {
        // best-effort
      }
      attemptRef.current = attempt;

      // Provider is genuinely unknown at this point — the popup is the
      // provider picker. We fire `signin_popup_opened` (intent) here and
      // let the popup-side `/sign-in` page fire `signup_initiated` with
      // provider when the user actually picks. Joining the two on
      // auth_attempt_id (preferred) or distinct_id gives us
      // iframe→popup→provider funnel without the double-count we'd get
      // if both fired `signup_initiated`.
      // Try/catch: analytics is best-effort, must never block window.open.
      try {
        posthog.capture('signin_popup_opened', {
          surface,
          pathname,
          ...buildIframeEventBase(attempt),
        });

        // If this sign-in originated from a login wall, fire the wall click
        // synchronously here — provider unknown but surface is. Wall
        // conversion = (login_wall_clicked count) / (login_wall_viewed count)
        // where both filter on the same surface.
        if (opts.wallType) {
          posthog.capture('login_wall_clicked', {
            surface,
            type: opts.wallType,
            pathname,
            ...buildIframeEventBase(attempt),
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[useAuth] capture failed (non-blocking)', err);
      }

      const mouseX = event.screenX;
      const mouseY = event.screenY;

      const left = Math.max(
        0,
        Math.min(mouseX - POPUP_WIDTH / 2, window.screen.width - POPUP_WIDTH),
      );
      const top = Math.max(
        0,
        Math.min(
          mouseY - POPUP_HEIGHT / 2,
          window.screen.height - POPUP_HEIGHT,
        ),
      );

      // Append `aa=<id>` so the popup-side pages thread this attempt's id:
      // /sign-in/verify carries it to /sign-in, /sign-in adopts it into the
      // popup tab's sessionStorage, and the callback reads it back from that
      // sessionStorage to stamp the iron-session. (The backend does NOT echo
      // `aa` on the callback redirect today — the stamp travels via the
      // same-tab sessionStorage record, not a URL echo.)
      //
      // `pf=extension` rides alongside: the sign-in physically completes on the
      // website (this popup), so nothing downstream can otherwise tell an
      // extension sign-in from a plain website one. `/sign-in` stores the
      // resolved platform on its attempt record and the callback page reads it
      // back for the first-party `UserEvent` row. Appended unconditionally —
      // it does not depend on an attempt id existing.
      const sep = WEBSITE_SIGN_IN_PATH.includes('?') ? '&' : '?';
      const popupUrl = appendPlatformHint(
        attempt
          ? `${WEBSITE_SIGN_IN_PATH}${sep}aa=${encodeURIComponent(attempt.id)}`
          : WEBSITE_SIGN_IN_PATH,
        EXTENSION_PLATFORM_HINT,
      );

      const popup = window.open(
        popupUrl,
        'SkimLandingAuth',
        `left=${left},top=${top},width=${POPUP_WIDTH},height=${POPUP_HEIGHT},resizable=yes,scrollbars=yes`,
      );

      if (!popup) {
        // Event 2 — auth_popup_blocked (iframe flow).
        safeCapture('auth_popup_blocked', buildIframeEventBase(attempt));
        // Tell the user why nothing happened. Without this the sign-in
        // button just silently resets when a popup blocker eats the OAuth
        // window — the user has no idea they need to allow popups, and we
        // lose the signup with zero feedback. (The website-side
        // `useOAuthPopup` already surfaces this via onError; the iframe
        // surface was silent.)
        toast.error(
          'Your browser blocked the sign-in popup. Please allow popups for this site and try again.',
        );
        setIsPending(false);
        return;
      }

      popup.focus();

      let authCompleted = false;

      // Handle COOP-severed popup references
      const isPopupClosed = () => {
        try {
          return !popup || popup.closed;
        } catch {
          return true;
        }
      };

      /**
       * Run the success flow given a session payload — extracted so both
       * the postMessage success path and the rescue refetch path can call
       * it. `viaRescue=true` tags the `signup_completed_client` event's
       * `rescued` prop so the funnel can quantify rescue load-bearing
       * rate (sibling of the extension-webapp's event-9 `rescued: true`
       * from CRE-781, prop-name standardized across surfaces 2026-06-11
       * late triple-check).
       */
      const completeSuccess = (
        sessionPayload: WebAppSessionType,
        viaRescue: boolean,
      ) => {
        if (authCompleted) return;
        authCompleted = true;
        receivedRef.current = true;

        // Identify against the NEW session — NOT the outer-scope `session`
        // from useSession(), which is the stale pre-sign-in value (null on
        // a first sign-in). Single identify per success — the earlier
        // duplicate identify against the stale outer value was a no-op-at-
        // best, mis-identify-at-worst (2026-06-11 late triple-check fix).
        // Fail-closed identity substrate (CRE-849): routed through
        // safeIdentify so a sentinel / anonymous / team id can never become
        // a PostHog person and corrupt the shared-project funnel — this
        // covers BOTH success paths (postMessage AND rescue refetch), since
        // completeSuccess is the single identify chokepoint.
        try {
          safeIdentify(sessionPayload.user.id, {
            email: sessionPayload.user.email,
            name: sessionPayload.user.username ?? undefined,
            picture: sessionPayload.user.picture ?? undefined,
          });
          posthog.capture('signup_completed_client', {
            surface,
            provider: sessionPayload.user.provider,
            ...buildIframeEventBase(attemptRef.current),
            rescued: viaRescue,
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[useAuth] identify/capture failed', err);
        }

        const sessionData: Session = {
          user: sessionPayload.user,
          tokens: {
            accessToken: sessionPayload.accessToken,
            refreshToken: sessionPayload.refreshToken,
            expiresAt: sessionPayload.expiresAt,
          },
          metadata: {
            createdAt: Date.now(),
            lastAccessedAt: Date.now(),
            source: 'iframe-message',
            version: '1.0.0',
            context: 'extension-iframe',
          },
        };

        const postMessage: PostMessageType = {
          origin: POST_MESSAGE_ORIGIN_TYPES.FROM_WEBAPP,
          to: POST_MESSAGE_DESTINATION_TYPES.TO_PUBLISHER,
          type: POST_MESSAGE_TYPES.USER_SESSION,
          message: sessionData,
        };
        window.parent.postMessage(postMessage, '*');

        // Event: auth_result_relayed_to_parent — the iframe's job is
        // not "received the auth-result" but "relayed it onward to the
        // browser extension host". This is the iframe-flow's terminal
        // success event (the extension proper handles its own session
        // storage from here).
        safeCapture('auth_result_relayed_to_parent', {
          ...buildIframeEventBase(attemptRef.current),
        });

        // Set the KNOWN-GOOD session into local state directly. We must NOT
        // call invalidateSessionState() here: it re-reads the session by
        // posting GET_SESSION to the extension, which RACES the USER_SESSION
        // write we just sent above — if the read wins, the extension hasn't
        // stored the session yet, getSession() returns null, and
        // invalidateSessionState() calls clearSession(), ACTIVELY logging the
        // user out right after a successful login. The iframe then stays
        // logged-out until the next SESSION_REVALIDATION_INTERVAL tick (3
        // minutes) — while the "Signed in successfully" toast already fired.
        // We already hold the validated session, so set it optimistically;
        // the extension store (via the USER_SESSION relay above) + the
        // periodic revalidation converge on it without the race.
        setSession(sessionData);
        toast.success('Signed in successfully');
        // Clear the spinner on BOTH success paths. The message path resets
        // isPending after its completeSuccess call, but the rescue path
        // returns immediately after completeSuccess — without this the
        // button spinner would hang until the next session-driven re-render.
        setIsPending(false);
      };

      /**
       * Rescue path (CRE-783 / PR-1b): on popup-closed-without-result, the
       * cookie may already be set on skim.plus even though the postMessage
       * notification dropped (COOP-severed opener / dropped message). Since
       * BroadcastChannel doesn't work cross-origin (iframe on
       * extension.skim.plus, popup on skim.plus), the only remaining
       * channel is to fetch `/api/session` directly — that endpoint is
       * CORS-allowed to EXTENSION_URL precisely so the iframe can read the
       * website's iron-session cookie via credentialed fetch.
       *
       * PROOF-OF-LOGIN GATE: "a session exists on skim.plus" is NOT proof
       * that THIS attempt's login succeeded. The iframe's logged-out state
       * mirrors the EXTENSION's session, not the cookie — so "iframe
       * logged out + valid web cookie" is a common state (e.g. the user
       * signed out of the extension earlier). A user who opens the popup
       * and closes it at the account picker has CANCELED; an ungated
       * rescue would adopt the pre-existing cookie session, identify() its
       * user, emit a false success, and push that session INTO the
       * extension — undoing an explicit extension sign-out on a cancel.
       *
       * The gate: the popup chain stamps this attempt's id into the
       * iron-session at mint time (sign-in page adopts our `?aa=`,
       * signInAction stores it, `/api/session` echoes it). The rescue only
       * completes when the fetched session's stamp matches `attemptRef`.
       * `?readonly=1` makes the probe side-effect-free (no token rotation
       * on a mere look); on a match the session was minted seconds ago so
       * its tokens are fresh by construction. No match → suppress: spinner
       * ends, nothing is relayed, `auth_rescue_suppressed` counts how
       * often the gate prevented a wrongful adoption. Fail-closed =
       * pre-#268 status quo, never adoption.
       */
      const attemptRescue = () => {
        // Bind to THIS attempt's record at schedule time so a rapid re-click
        // (which overwrites attemptRef.current) can't make this timer
        // mis-attribute its telemetry or clear the new attempt's spinner
        // (RACE-1). The handle is stored so cleanup()/a new handleSignIn can
        // cancel an in-flight rescue.
        const rescueAttempt = attemptRef.current;
        rescueTimeoutRef.current = setTimeout(async () => {
          rescueTimeoutRef.current = null;
          if (receivedRef.current || authCompleted) return;
          try {
            const response = await fetch(
              `${env.NEXT_PUBLIC_WEBSITE_APP_URL}/api/session?readonly=1`,
              {
                method: 'GET',
                credentials: 'include',
                headers: { Accept: 'application/json' },
              },
            );
            if (!response.ok) {
              setIsPending(false);
              return;
            }
            const data = (await response.json()) as {
              session?: (WebAppSessionType & { error?: string }) | null;
            };
            const fetched = data?.session;
            // Never adopt a known-broken-refresh session, even on a stamp
            // match (parity with the readonly route + verify-page; ENV-5).
            if (fetched?.accessToken && fetched.error !== 'RefreshTokenError') {
              const attemptId = rescueAttempt?.id ?? null;
              if (isSessionProvenByAttempt(fetched.authAttemptId, attemptId)) {
                completeSuccess(fetched, true);
                return;
              }
              // Session exists but was NOT minted by this attempt —
              // pre-existing cookie (canceled popup), legacy un-stamped
              // session, or the popup chain didn't propagate our id.
              // Suppress: completing here is the adoption hazard. `rescued:
              // true` keeps ALL rescue-path events one-filter queryable per
              // the standing convention; the SUCCESS metric filters on event
              // NAME (signup_completed_client/auth_session_synced + rescued),
              // so a suppression never pollutes it.
              safeCapture('auth_rescue_suppressed', {
                ...buildIframeEventBase(rescueAttempt),
                reason: attemptId ? 'attempt_mismatch' : 'no_attempt_record',
                session_stamp_present: fetched.authAttemptId != null,
                rescued: true,
              });
            }
          } catch (err) {
            // Rescue is best-effort; network/CORS failure → fall through
            // to the normal "popup closed" reset.
            // eslint-disable-next-line no-console
            console.warn('[useAuth] rescue refetch failed', err);
          }
          // No session on rescue (or no proof it was ours) — popup-close
          // was a real abandon as far as this attempt is concerned.
          setIsPending(false);
        }, RESCUE_REFETCH_DELAY_MS);
      };

      /**
       * Stop the interval immediately (popup is closed, no more state to
       * observe) but leave the message listener alive for LISTENER_GRACE_MS
       * so queued postMessage events drain. After the grace expires, if no
       * message arrived, kick off the rescue refetch.
       */
      const scheduleGracefulCleanup = () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (graceTimeoutRef.current) return;
        graceTimeoutRef.current = setTimeout(() => {
          graceTimeoutRef.current = null;
          if (receivedRef.current) return;
          // Event 8 — auth_popup_closed_without_result (iframe surface,
          // Seam B+A counter per tracking-plan-v2 §4 row 8; the landing
          // iframe mirrors events 1/2/6/7/8). Fires BEFORE the rescue so
          // the funnel records that the rescue path was needed regardless
          // of how the rescue resolves.
          safeCapture('auth_popup_closed_without_result', {
            ...buildIframeEventBase(attemptRef.current),
            close_path: 'closed_poll',
          });
          // Tear down the message listener now — popup is gone and the
          // grace window expired without a message arriving.
          if (listenerRef.current) {
            window.removeEventListener('message', listenerRef.current);
            listenerRef.current = null;
          }
          attemptRescue();
        }, LISTENER_GRACE_MS);
      };

      // Poll for popup close — hand off to graceful cleanup so the message
      // listener gets a chance to drain any queued event before we tear
      // it down + attempt the session rescue.
      intervalRef.current = setInterval(() => {
        if (isPopupClosed()) {
          scheduleGracefulCleanup();
        }
      }, 250);

      // Listen for auth-result from the website's sign-in/callback/verify pages
      listenerRef.current = (msgEvent: MessageEvent<AuthMessage>) => {
        // Origin gating with explicit telemetry on rejection (Seam C).
        if (
          msgEvent.data?.type === 'auth-result' &&
          msgEvent.origin !== env.NEXT_PUBLIC_APP_URL &&
          msgEvent.origin !== env.NEXT_PUBLIC_WEBSITE_APP_URL
        ) {
          let rejectedHost = '';
          try {
            rejectedHost = new URL(msgEvent.origin).host;
          } catch {
            rejectedHost = msgEvent.origin;
          }
          safeCapture('auth_origin_rejected', {
            ...buildIframeEventBase(attemptRef.current),
            rejected_origin_host: rejectedHost,
            expected_host: env.NEXT_PUBLIC_APP_URL,
            expected_host_alt: env.NEXT_PUBLIC_WEBSITE_APP_URL,
          });
          return;
        }
        if (
          msgEvent.origin !== env.NEXT_PUBLIC_APP_URL &&
          msgEvent.origin !== env.NEXT_PUBLIC_WEBSITE_APP_URL
        ) {
          return;
        }
        if (msgEvent.data.type !== 'auth-result') return;
        if (receivedRef.current) return;

        receivedRef.current = true;
        // NOTE: do NOT set `authCompleted = true` here. completeSuccess()
        // (below) OWNS that flag — its first line is `if (authCompleted)
        // return`. Setting it here made the completeSuccess() call on the
        // PRIMARY postMessage success path a SILENT NO-OP: no extension
        // relay (window.parent.postMessage), no setSession, no success toast,
        // no signup_completed_client. That is the real "popup completes but
        // the session never reaches the webapp/extension" failure — present
        // since the round-2 completeSuccess refactor (CRE-783). `receivedRef`
        // alone dedupes listener re-entry AND suppresses the rescue (the
        // rescue guard checks `receivedRef.current || authCompleted`), so
        // completeSuccess stays the single owner of `authCompleted`.

        // Event 6 — auth_result_received (iframe flow).
        safeCapture('auth_result_received', {
          ...buildIframeEventBase(attemptRef.current),
          via: 'postMessage',
          success: msgEvent.data.success,
          popup_was_closed: isPopupClosed(),
        });

        cleanup();

        if (!isPopupClosed()) {
          try {
            popup.close();
          } catch {
            // COOP-severed
          }
        }

        if (msgEvent.data.success && msgEvent.data.session) {
          completeSuccess(msgEvent.data.session, false);
        } else {
          const errorMsg =
            msgEvent.data.error || 'Sign-in failed. Please try again.';

          safeCapture('signup_failed', {
            ...buildIframeEventBase(attemptRef.current),
            surface,
            reason: 'oauth_callback_failed',
          });
          console.error('useAuth: Authentication failed:', errorMsg);
          toast.error(errorMsg);
        }

        setIsPending(false);
      };

      window.addEventListener('message', listenerRef.current);
    },
    [cleanup, setSession, pathname],
  );

  const handleSignOut = useCallback(async () => {
    setIsPending(true);
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_WEBSITE_APP_URL}/api/clear-cookies`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to clear cookies: ${response.status} ${response.statusText}`,
        );
      }

      await clearSession();
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('useAuth: handleSignOut error:', error);
    } finally {
      // Reset PostHog identity regardless of whether the cookie clear
      // succeeded — the user's intent to sign out is unambiguous, and
      // leaving the identified person on this device means the NEXT
      // person's anonymous events merge into the signed-out user
      // (cross-user person contamination on shared browsers — audit
      // finding C1). The website app does the equivalent via
      // PosthogIdentityBridge; the landing app has no bridge, so the
      // sign-out chokepoint is the right place.
      try {
        posthog.reset();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[useAuth] posthog.reset failed (non-blocking)', err);
      }
      setIsPending(false);
    }
  }, [clearSession]);

  return {
    isAuthenticated: !!session?.tokens.accessToken,
    isPending,
    handleSignIn,
    handleSignOut,
  };
};
